/**
 * HoursCard — two-panel hours grid card.
 *
 * Fixed 176px label column on the left + fluid 7-column day grid on the right.
 * Cell widths are calculated from the container's available width via onLayout,
 * falling back to 120px.
 *
 * Renders two row types:
 *   - toggle: read-only boolean field (Closed / Non-Op, Open 24hr). Clicking in edit mode
 *             triggers the ReadOnlyPopover.
 *   - editable: time-range field. Tapping in edit mode opens the CellEditModal. 24hr-day
 *               drive-thru/dining-room cells are disabled (server overrides from DOP block extents).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, HStack, Pressable, VStack } from 'native-base';

import { Lock01Icon, PencilIcon } from '@pimm/base';
import { Text } from '@pimm/base';
import { ActiveCell, CELL_W_FALLBACK, DailyHoursShape, DAYS_SHORT, EDITABLE_ROW_CONFIG, HEADER_H, LABEL_W, ROW_H } from '../types';
import { formatTime } from '../utils';

type Row = { type: 'toggle'; label: string; field: keyof DailyHoursShape } | { type: 'editable'; rowKey: string };

type Props = {
  title: string;
  rows: Row[];
  storeHours: DailyHoursShape[];
  displayHours: DailyHoursShape[];
  editMode: boolean;
  activeCell: ActiveCell | null;
  dirtySet: Set<string>;
  onCellClick: (rowKey: string, dayIdx: number) => void;
  onReadOnlyClick: () => void;
  closedDayIndices: Set<number>;
  orderedIndices: number[];
};

export const HoursCard = ({
  title,
  rows,
  storeHours,
  displayHours,
  editMode,
  activeCell,
  dirtySet,
  onCellClick,
  onReadOnlyClick,
  closedDayIndices,
  orderedIndices,
}: Props) => {
  const { t } = useTranslation();
  const [cardWidth, setCardWidth] = useState(0);
  const cellW = cardWidth > LABEL_W ? Math.floor((cardWidth - LABEL_W) / 7) : CELL_W_FALLBACK;

  return (
    <Box
      bg="white"
      rounded="lg"
      borderWidth={1}
      borderColor="gray.200"
      overflow="hidden"
      shadow={1}
      onLayout={(e: any) => setCardWidth(e.nativeEvent.layout.width)}
    >
      {/* Card title bar */}
      <Box px={3} py={2} borderBottomWidth={1} borderColor="gray.200">
        <Text size="md" fontWeight={600} color="gray.900">
          {title}
        </Text>
      </Box>

      {/* Two-panel grid */}
      <HStack overflow="hidden">
        {/* Fixed label column */}
        <VStack w={`${LABEL_W}px`} flexShrink={0} borderRightWidth={1} borderColor="gray.200" bg="white" zIndex={1}>
          {/* Empty header cell aligning with the day-name row */}
          <Box w={`${LABEL_W}px`} h={`${HEADER_H}px`} bg="gray.50" borderBottomWidth={1} borderColor="gray.200" />
          {/* Row label cells */}
          {rows.map(row =>
            row.type === 'toggle' ? (
              <Box
                key={row.field}
                w={`${LABEL_W}px`}
                h={`${ROW_H}px`}
                justifyContent="center"
                bg="white"
                borderBottomWidth={1}
                borderColor="gray.100"
              >
                <HStack alignItems="center" space={1} px={3}>
                  <Box w={1} bg="transparent" />
                  <Text size="md" fontWeight={600} color="gray.700">
                    {row.label}
                  </Text>
                </HStack>
              </Box>
            ) : (
              <Box
                key={row.rowKey}
                w={`${LABEL_W}px`}
                h={`${ROW_H}px`}
                justifyContent="center"
                bg="white"
                borderBottomWidth={1}
                borderColor="gray.100"
              >
                <HStack alignItems="center" h="full">
                  {/* Blue left-border strip on rows with pending changes */}
                  <Box
                    w={1}
                    h="full"
                    bg={editMode && displayHours.some((_, i) => dirtySet.has(`${row.rowKey}-${i}`)) ? 'blue.500' : 'transparent'}
                  />
                  <Box px={3} flex={1}>
                    <Text size="md" fontWeight={600} color="gray.700">
                      {EDITABLE_ROW_CONFIG[row.rowKey]?.label ? t(EDITABLE_ROW_CONFIG[row.rowKey].label) : row.rowKey}
                    </Text>
                  </Box>
                </HStack>
              </Box>
            ),
          )}
        </VStack>

        {/* Day columns — fill remaining width */}
        <Box flex={1}>
          <VStack>
            {/* Day-name header row */}
            <HStack>
              {orderedIndices.map(protoIdx => (
                <Box
                  key={protoIdx}
                  w={`${cellW}px`}
                  h={`${HEADER_H}px`}
                  bg="gray.50"
                  alignItems="center"
                  justifyContent="center"
                  borderRightWidth={1}
                  borderBottomWidth={1}
                  borderColor="gray.200"
                >
                  <Text size="sm" fontWeight={600} color={closedDayIndices.has(protoIdx) ? 'gray.400' : 'gray.600'}>
                    {DAYS_SHORT[protoIdx]}
                  </Text>
                </Box>
              ))}
            </HStack>

            {/* Data rows */}
            {rows.map(row =>
              row.type === 'toggle' ? (
                // Read-only boolean row (Closed / Non-Op, Open 24hr)
                <HStack key={row.field} alignItems="stretch">
                  {orderedIndices.map(protoIdx => {
                    const h = storeHours[protoIdx];
                    return (
                      <Pressable key={protoIdx} onPress={editMode ? onReadOnlyClick : undefined}>
                        <Box
                          w={`${cellW}px`}
                          h={`${ROW_H}px`}
                          alignItems="center"
                          justifyContent="center"
                          bg={closedDayIndices.has(protoIdx) || editMode ? 'gray.50' : undefined}
                          borderRightWidth={1}
                          borderBottomWidth={1}
                          borderColor="gray.100"
                        >
                          {(h as any)[row.field] === null ? (
                            <Text size="md" color="gray.300">
                              —
                            </Text>
                          ) : (h as any)[row.field] ? (
                            <Text size="md" fontWeight={600} color="gray.800">
                              {t('store_settings:yes')}
                            </Text>
                          ) : (
                            <Text size="md" color="gray.400">
                              {t('store_settings:no')}
                            </Text>
                          )}
                        </Box>
                      </Pressable>
                    );
                  })}
                </HStack>
              ) : (
                (() => {
                  // Editable time-range row
                  const cfg = EDITABLE_ROW_CONFIG[row.rowKey];
                  if (!cfg) return null;
                  return (
                    <HStack key={row.rowKey} alignItems="stretch">
                      {orderedIndices.map(protoIdx => {
                        const h = displayHours[protoIdx];
                        const isEnabled = !cfg.enabledField || !!h[cfg.enabledField as keyof DailyHoursShape];
                        const isActive = activeCell?.rowKey === row.rowKey && activeCell?.dayIdx === protoIdx;
                        const isDirty = dirtySet.has(`${row.rowKey}-${protoIdx}`);
                        const isClosed = closedDayIndices.has(protoIdx);
                        const is24hr = h.open24Hrs;
                        // 24hr days: disable drive-thru and dining-room inputs (server overrides from DOP blocks)
                        const isLocked24 = is24hr && (row.rowKey === 'diningRoom' || row.rowKey === 'driveThru');
                        const isEditable = editMode && isEnabled && !isClosed && !isLocked24;

                        if (!isEnabled || isClosed) {
                          return (
                            <Pressable key={protoIdx} onPress={editMode && !isClosed ? onReadOnlyClick : undefined}>
                              <Box
                                w={`${cellW}px`}
                                h={`${ROW_H}px`}
                                alignItems="center"
                                justifyContent="center"
                                px={2}
                                bg="gray.50"
                                borderRightWidth={1}
                                borderBottomWidth={1}
                                borderColor="gray.100"
                              >
                                <Text size="md" color="gray.400">
                                  {t(cfg.noServiceLabel)}
                                </Text>
                              </Box>
                            </Pressable>
                          );
                        }

                        const startVal = (h as any)[cfg.openField] as string;
                        const endVal = (h as any)[cfg.closeField] as string;
                        const start = formatTime(startVal);
                        const end = formatTime(endVal);

                        if (!isEditable) {
                          return (
                            <Pressable key={protoIdx} onPress={isLocked24 && editMode ? onReadOnlyClick : undefined}>
                              <Box
                                w={`${cellW}px`}
                                h={`${ROW_H}px`}
                                alignItems="center"
                                justifyContent="center"
                                px={2}
                                bg={isLocked24 && editMode ? 'gray.50' : undefined}
                                borderRightWidth={1}
                                borderBottomWidth={1}
                                borderColor="gray.100"
                              >
                                <Text size="md" fontWeight={500} color={isLocked24 ? 'gray.400' : 'gray.900'}>
                                  {start}
                                  <Text size="md" color="gray.400">
                                    {' '}
                                    –{' '}
                                  </Text>
                                  {end}
                                </Text>
                                {isLocked24 && editMode && <Lock01Icon size={3} color="gray.300" />}
                              </Box>
                            </Pressable>
                          );
                        }

                        return (
                          <Pressable key={protoIdx} onPress={() => onCellClick(row.rowKey, protoIdx)}>
                            <Box
                              w={`${cellW}px`}
                              h={`${ROW_H}px`}
                              alignItems="center"
                              justifyContent="center"
                              px={2}
                              bg={isActive ? 'blue.100' : isDirty ? 'blue.50' : undefined}
                              borderRightWidth={1}
                              borderBottomWidth={1}
                              borderColor="gray.100"
                            >
                              <HStack alignItems="center" space={2} px={2}>
                                <Text
                                  size="md"
                                  fontWeight={500}
                                  color="gray.900"
                                  borderWidth={1}
                                  borderColor={isActive ? 'blue.500' : 'transparent'}
                                  rounded="sm"
                                  px={1}
                                >
                                  {start}
                                  <Text size="md" color="gray.400">
                                    {' '}
                                    –{' '}
                                  </Text>
                                  {end}
                                </Text>
                                {isDirty && !isActive && <Box w={1.5} h={1.5} rounded="full" bg="blue.500" />}
                                {!isDirty && !isActive && <PencilIcon size={4} color="primary.500" />}
                              </HStack>
                            </Box>
                          </Pressable>
                        );
                      })}
                    </HStack>
                  );
                })()
              ),
            )}
          </VStack>
        </Box>
      </HStack>
    </Box>
  );
};
